import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";


export type OnboardingStepId =
  | "profile"
  | "upload"
  | "invite"
  | "social"
  | "cashout";

export interface OnboardingStep {
  id: OnboardingStepId;
  label: string;
  description: string;
  tab: string;
  subtab?: string;
  target: string; // data-tour attribute value
  done: boolean;
}

interface ProgressData {
  uploadedCount: number;
  inviteCount: number;
  socialCount: number;
  hasCashout: boolean;
  kycVerified: boolean;
  profileComplete: boolean;
}

export function useOnboardingProgress() {
  const { user } = useAuth();

  const { data, isLoading } = useQuery<ProgressData>({
    queryKey: ["onboarding-progress", user?.id],
    enabled: !!user?.id,
    staleTime: 60_000,
    queryFn: async (): Promise<ProgressData> => {
      const uid = user!.id;

      const sb = supabase as any;

      const uploadedRes = await sb
        .from("tracks")
        .select("id", { count: "exact", head: true })
        .eq("artist_id", uid);

      const inviteRes = await sb
        .from("sponsor_artist_invites")
        .select("id", { count: "exact", head: true })
        .eq("artist_id", uid);

      const socialRes = await sb
        .from("social_connections")
        .select("id", { count: "exact", head: true })
        .eq("user_id", uid);

      const cashoutRes = await sb
        .from("cashout_requests")
        .select("id", { count: "exact", head: true })
        .eq("artist_id", uid);

      const artistRes = await sb
        .from("artist_profiles")
        .select("kyc_status")
        .eq("id", uid)
        .maybeSingle();

      const publicRes = await sb
        .from("public_profiles")
        .select("display_name, bio, avatar_url, location")
        .eq("id", uid)
        .maybeSingle();

      const pp = (publicRes.data ?? {}) as {
        display_name?: string | null;
        bio?: string | null;
        avatar_url?: string | null;
        location?: string | null;
      };
      const nonEmpty = (v?: string | null) => !!v && v.trim() !== "";
      const profileComplete =
        nonEmpty(pp.display_name) &&
        nonEmpty(pp.bio) &&
        nonEmpty(pp.avatar_url) &&
        nonEmpty(pp.location);

      const kycStatus = (
        artistRes.data as { kyc_status?: string } | null
      )?.kyc_status;

      return {
        uploadedCount: uploadedRes.count ?? 0,
        inviteCount: inviteRes.count ?? 0,
        socialCount: socialRes.count ?? 0,
        hasCashout: (cashoutRes.count ?? 0) > 0,
        kycVerified: kycStatus === "approved" || kycStatus === "verified",
        profileComplete,
      };
    },
  });


  const steps: OnboardingStep[] = [
    {
      id: "profile",
      label: "Complete your profile",
      description: "Open Edit Profile to add your name, bio, avatar, location and genres.",
      tab: "dashboard",
      target: "onboarding-profile",
      done: !!data?.profileComplete,
    },
    {
      id: "upload",
      label: "Upload your first track",
      description: "Add a song to your catalog to start earning.",
      tab: "tracks",
      subtab: "upload",
      target: "onboarding-upload",
      done: (data?.uploadedCount ?? 0) >= 1,
    },
    {
      id: "invite",
      label: "Invite a sponsor",
      description: "Pitch your tracks to potential sponsors.",
      tab: "sponsorships",
      target: "onboarding-invite",
      done: (data?.inviteCount ?? 0) >= 1,
    },
    {
      id: "social",
      label: "Connect a social account",
      description: "Link Instagram, X or Facebook to boost your reach.",
      tab: "social-connect",
      target: "onboarding-social",
      done: (data?.socialCount ?? 0) >= 1,
    },
    {
      id: "cashout",
      label: "Set up cash out",
      description: "Verify KYC and add your bank to receive earnings.",
      tab: "kyc",
      target: "onboarding-cashout",
      done: !!data?.kycVerified && !!data?.hasCashout,
    },
  ];

  const completedCount = steps.filter((s) => s.done).length;
  const total = steps.length;

  return {
    steps,
    completedCount,
    total,
    percentage: Math.round((completedCount / total) * 100),
    isLoading,
    allDone: completedCount === total,
  };
}
